import { NavMain } from "@/app/dashboard/nav-main"
import { NavSecondary } from "@/app/dashboard/nav-secondary"
import { NavUser } from "@/app/dashboard/nav-user"
import BrandLogo from "@/shared/components/BrandLogo"
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
} from "@/shared/ui/sidebar"
import { useIsAdmin } from "@/domains/admin/hooks/useIsAdmin"
import { BellDot, Cable, Lightbulb, ShieldCheck, SwatchBook, Table2, User, Zap } from "lucide-react"

interface AppSidebarProps {
  activeTab: string
  onTabChange: (tabId: string) => void
  profile?: {
    name?: string
    email?: string
    picture?: string
    avatar?: string
    photo?: string
    plan?: string
  } | null
}

export function AppSidebar({ activeTab, onTabChange, profile }: AppSidebarProps) {
  const { isAdmin } = useIsAdmin(Boolean(profile))
  // Get avatar from profile picture or social login photo
  const getProfileAvatar = () => {
    return profile?.picture || profile?.avatar || profile?.photo || ""
  }
  const isToolboxPlan = (profile?.plan || "").toLowerCase() === "toolbox"
  const optionsFlowNavigation = [{
    title: "Options Flow",
    url: "#",
    icon: Zap,
    isActive: activeTab === "live-options" || activeTab === "zdte",
    items: [
      {
        title: "Live Options",
        url: "#",
        isActive: activeTab === "live-options",
        onClick: () => onTabChange("live-options"),
      },
      {
        title: "0 DTE Flow",
        url: "#",
        isActive: activeTab === "zdte",
        onClick: () => onTabChange("zdte"),
      },
    ],
  }]

  const data = {
    user: {
      name: profile?.name || "User",
      email: profile?.email || "",
      avatar: getProfileAvatar(),
    },
    navMain: [
      {
        title: "Overview",
        url: "#",
        icon: Table2,
        isActive: activeTab === "overview",
        onClick: () => onTabChange("overview"),
      },
      {
        title: "Watches",
        url: "#",
        icon: SwatchBook,
        isActive: !isToolboxPlan && (activeTab === "watches" || activeTab === "watches-create"),
        onClick: () => !isToolboxPlan && onTabChange("watches"),
        disabled: isToolboxPlan,
        badgeText: isToolboxPlan ? "Upgrade" : undefined,
      },
      {
        title: "Trade Ideas",
        url: "#",
        icon: Lightbulb,
        isActive:
          !isToolboxPlan
          && (activeTab === "trade-ideas" || activeTab === "trade-ideas-edition" || activeTab === "trade-ideas-ledger"),
        onClick: () => !isToolboxPlan && onTabChange("trade-ideas"),
        disabled: isToolboxPlan,
        badgeText: isToolboxPlan ? "Upgrade" : undefined,
      },
      ...optionsFlowNavigation,
      {
        title: "Alert Preferences",
        url: "#",
        icon: BellDot,
        isActive: !isToolboxPlan && activeTab === "notifications",
        onClick: () => !isToolboxPlan && onTabChange("notifications"),
        disabled: isToolboxPlan,
        badgeText: isToolboxPlan ? "Upgrade" : undefined,
      },
      {
        title: "Connect with mcp",
        url: "#",
        icon: Cable,
        isActive: activeTab === "connector",
        onClick: () => onTabChange("connector"),
      },
      ...(isAdmin
        ? [{
            title: "Admin",
            url: "#",
            icon: ShieldCheck,
            isActive: activeTab === "metrics" || activeTab === "ai-metrics" || activeTab === "subscriptions" || activeTab === "task-demo",
            items: [
              {
                title: "User Metrics",
                url: "#",
                isActive: activeTab === "metrics",
                onClick: () => onTabChange("metrics"),
              },
              {
                title: "Subscriptions",
                url: "#",
                isActive: activeTab === "subscriptions",
                onClick: () => onTabChange("subscriptions"),
              },
              {
                title: "AI Metrics",
                url: "#",
                isActive: activeTab === "ai-metrics",
                onClick: () => onTabChange("ai-metrics"),
              },
              {
                title: "Tasks",
                url: "#",
                isActive: activeTab === "task-demo",
                onClick: () => onTabChange("task-demo"),
              },
            ],
          }]
        : []),
    ],
    navSecondary: [
      {
        title: "Account",
        url: "#",
        icon: User,
        isActive: activeTab === "account",
        onClick: () => onTabChange("account"),
      },
    ],
  }

  return (
    <Sidebar>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              asChild
              className="hover:bg-transparent active:bg-transparent"
            >
              <div className="flex items-center gap-2">
                <BrandLogo showWordmark={false} size={32} />
                <div className="flex flex-col gap-0.5 leading-none">
                  <span className="font-semibold">LinkedHash</span>
                </div>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={data.navMain} />
        <NavSecondary items={data.navSecondary} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser
          user={data.user}
          loading={!profile}
          onNavigate={onTabChange}
        />
      </SidebarFooter>
    </Sidebar>
  )
}
